import React, {useContext, useState} from 'react'
import PropTypes from 'prop-types'

import AllroundContainer from '../../_layouts/AllroundContainer'

import { ContextHeadline, TextBlock } from '@aok/text-elements'
import { InfoCircle } from '@aok/icon-functional'

import { EasyLanguagePageContext } from '../../../context/EasyLanguagePageContext'
import { SignLanguagePageContext } from '../../../context/SignLanguagePageContext'
import defaultProps from '../../../utils/propTypes/defaultProps'
import LinkTitle from '../../atoms/LinkTitle/LinkTitle'
import { SecondaryButton } from '@aok/ui-elements'

import LexiconTextBlock from '../../molecules/LexiconTextBlock/LexiconTextBlock'
import { RenderImage } from '../../molecules/ImageOverlay/RenderImage'

const Y012 = ({
  label,
  headline,
  text,
  addedInfo,
  className,
  standsAlone,
  onCloseButtonClick,
  lexicon,
  settings,
  image,
  zoomImage,
}) => {
  const { isEasyLanguagePage } = useContext(EasyLanguagePageContext)
  const { isSignLanguagePage } = useContext(SignLanguagePageContext)
  const [visible, setVisible] = useState(true)

  return (
    <AllroundContainer>
      {visible && (
        <div
          data-tracking-name="Y012"
          className={`bg-f8f5e3 flex flex-col gap-y-8 px-16 py-24 relative
        760:py-32 760:px-24
        1000:py-40 1000:px-32
        ${className || ''}
      `}
        >
          <div className="flex flex-col 760:flex-row gap-16 760:gap-24 1000:gap-32">
            {/* Text Area */}
            <div className="flex-1">
            {label && (
              <div
                className={`mb-8 text-005e3f flex items-center gap-8
                ${isEasyLanguagePage && !isSignLanguagePage ? 'leading-32 text-16' : 'leading-20 text-14'}
              `}
              >
                <InfoCircle className="shrink-0 w-12 h-12 fill-current" fill="#005e3f"/>
                <p>{label}</p>
              </div>
            )}

            {headline?.url ? (
              <LinkTitle tag="h5" url={headline.url} title={headline.text}/>
              ) : !standsAlone ? (
                  <ContextHeadline
                    {...headline}
                    id={headline.id}
                    className={`font-semibold text-293033 text-20
                    ${isEasyLanguagePage && !isSignLanguagePage ? 'accessibilityHeadline h5' : 'leading-28'}
                  `}
                  />
                ) : (
                  <p
                    id={headline.id}
                    className={`font-semibold text-293033 text-20
                    ${isEasyLanguagePage && !isSignLanguagePage ? 'headline accessibilityHeadline h5' : 'leading-28'}
                  `}
                  >
                    {headline.text}
                  </p>
                )}

              {/* TextBlock / LexiconTextBlock */}
              <div className="flex items-center gap-8 mt-8">
                {!isSignLanguagePage && isEasyLanguagePage && lexicon && text ? (
                  <LexiconTextBlock
                    className={`w-full ${isEasyLanguagePage && 'mb-8 600:mb-0 accessibilityParagraph'}`}
                    text={text}
                    lexicon={lexicon}
                  />
                ) : (
                  <TextBlock className={`-structure ${isEasyLanguagePage && 'accessibilityParagraph'} ${isSignLanguagePage && 'setSignLangIcon leading-24 text-16'}`} text={text} />
            )}
            </div>
            {isSignLanguagePage && (
                <SecondaryButton
                  className="mt-32 w-auto  760:w-auto 360:w-full"
                  title="Verstanden und Schließen"
                  buttonSize="small"
                  onClick={() => {
                setVisible(false)
                    onCloseButtonClick?.()
                  }}
                >
                Verstanden und Schließen
              </SecondaryButton>
            )}
          </div>

            {/* Image */}
            {isEasyLanguagePage && image && settings && zoomImage && (
              <div className="flex-1 1000:max-w-352">
                <RenderImage
                  image={image}
                  settings={settings}
                  text={text}
                  zoomImage={zoomImage}
                  dataTrackingName="Y012"
                />
              </div>
            )}
          </div>
        </div>
      )}
      {/* Additional Info */}
      {addedInfo && visible && (
        <TextBlock
          className={`-structure mt-24 ml-16 600:ml-32 text-6d767c
            ${isEasyLanguagePage && 'accessibilityParagraph'}
          `}
          text={addedInfo}
        />
      )}
    </AllroundContainer>
  )
}

Y012.propTypes = {
  label: PropTypes.string,
  headline: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    url: PropTypes.string,
  }),
  text: PropTypes.string,
  addedInfo: PropTypes.string,
  className: PropTypes.string,
  standsAlone: PropTypes.bool,
  onCloseButtonClick: PropTypes.func,
  lexicon: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      link: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ),
  settings: PropTypes.shape({
    grid: PropTypes.oneOf(['full', 'content']),
    alignment: PropTypes.oneOf(['left', 'right']),
    width: PropTypes.oneOf(['full', 'half', 'third', 'quarter']),
    isPortraitFormat: PropTypes.bool,
  }),
  image: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        breakpoint: PropTypes.number,
      })
    ),
    alt: PropTypes.string,
    copyright: PropTypes.string,
    contentUrl: PropTypes.string,
    caption: PropTypes.string,
  }),
  zoomImage: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        breakpoint: PropTypes.number,
      })
    ),
    alt: PropTypes.string,
    copyright: PropTypes.string,
    caption: PropTypes.string,
  }),
}

Y012.defaultProps = {
  label: '',
  headline: { text: defaultProps.HEADLINE },
  text: defaultProps.TEXT,
  addedInfo: '',
  className: '',
  standsAlone: true,
  lexicon: [],
  settings: {
    width: 'full',
    alignment: 'left',
    grid: 'content',
  },
}

export default Y012
